<template>
    <div v-if="label">
        <h2 class="subtitle">{{label.titles.title}}
          <Info multilined bottom 
          tooltipText="Les évènements que vous encodez sont visibles dans le graphe des consommations.
          Ils peuvent vous servir de repère pour visualiser l'impact d'un évènement sur votre consommation.
          Par exemple : Installation d'une borne électrique"/>
        </h2>
        <template v-if="loadedLocations">
          <template v-if="locations && locations.length > 0">
            <!-- location selection -->
            <div class="field">
              <label class="label">{{label.texts.address}} <Info multilined :tooltipText="label.texts.addressTooltip"/></label>
              <div class="control">
                <div class="dropdown" :class="{'is-active': locationDropActive}">
                  <div class="dropdown-trigger">
                    <button
                      class="button"
                      type="button"
                      @click="locationDropActive = !locationDropActive"
                      @blur="locationDropActive = false">
                      <span>{{locationText}}</span>
                      <span class="icon is-small"><awesome-icon icon="angle-down" aria-hidden="true"/></span>
                    </button>
                  </div>
                  <div class="dropdown-menu " role="menu">
                    <div class="dropdown-content">
                      <a
                        class="dropdown-item"
                        v-for="location of locations"
                        :key="location.id"
                        @mousedown="loadLocations(location.id)"
                      >
                        {{formatAddress(location)}}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- events table -->
            <div class="table-container">
              <table class="table is-hoverable">
                <thead>
                    <tr>
                      <th>{{label.forms.date}}</th>
                      <th>{{label.forms.category}}</th>
                      <!-- <th>{{label.forms.type}}</th> -->
                      <th>{{label.forms.description}}</th>
                      <th>{{label.forms.actions}}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                      <td
                          class="add-event has-text-centered"
                          colspan="20"
                          @click="createEventModal()">
                          <awesome-icon class="button-icon" icon="plus"/>{{label.buttons.addEvent}}
                      </td>
                    </tr>
                    <tr v-for="event of events" :key="event.id">
                      <td>{{prettyFormatDate(event.date)}}</td>
                      <td>{{event.category}}</td>
                      <!-- <td>{{event.type ? event.type : '--'}}</td> -->
                      <td>{{event.description ? event.description : '--'}}</td>
                      <td class="has-text-centered">
                          <a class="button is-outlined is-info is-small" style="margin-right: 10px;" @click="editEventModal(event)">{{label.buttons.modify}}</a>
                          <a class="button is-outlined is-danger is-small" @click="deleteEvent(event.id)">{{label.buttons.delete}}</a>
                      </td>
                    </tr>
                </tbody>
              </table>
            </div>

            <!-- event creation/edition modal -->
            <EventModal
              :openModal="showModalEvent"
              :showCreationForm="showCreationForm"
              :locationID="selectedLocationID"
              :event="selectedEvent"
              :reloadData="loadEvents"
              :closeModal="closeModal"
            />

            <!-- Pagination -->
            <div class="field is-grouped">
                <div class="control">
                    <button
                      :disabled="index <= 0"
                      class="button is-small"
                      @click="index--">{{label.buttons.previous}}</button>
                </div>
                <div class="control"><span class="is-size-7"><strong>{{label.texts.page}} {{this.index + 1}} {{ this.nbPages ? '/ ' + this.nbPages : ''}}</strong></span></div>
                <div class="control">
                    <button
                      :disabled="isLastPage"
                      class="button is-small"
                      @click="index++">{{label.buttons.next}}</button>
                </div>
            </div>
          </template>
          <template v-else>
            <div class="notification is-warning">
              Votre compte doit être associé à une adresse pour pouvoir encoder des évènements.
              <br>
              Pour cela, vous pouvez encoder une demande d'accès dans 
              <a @click="$router.push('/profile/pods', () => {})">
                <strong>"Mon profil -> Mes points d'accès"</strong>
              </a>.
            </div>
          </template>
        </template>
        <template v-else>
          {{label.texts.loadingAddresses}}
        </template>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { EventService, AddressService, PODService } from '../services'
import EventModal from '../components/EventModal'
import Info from '../components/Info.vue'
import moment from 'moment'

export default {
    name: 'MyEvents',
    components: {
        EventModal,
        Info
    },
    data() {
        return {
            events: null,
            showCreationForm: false,
            selectedLocationID: null,
            locations: null,
            clipods: null,
            loadedLocations: false,
            index: 0,
            number: 10,
            nbPages: null,
            loadingEvents: false,
            locationDropActive: false,
            locationText: "",
            showModalEvent: false,
            selectedEvent: null
        }
    },
    computed: {
        ...mapState("account", ["user"]),
        ...mapState({ label: state => state.labels.myEvents }),
        isLastPage() {
            return this.nbPages && (this.index + 1 >= this.nbPages)
        }
    },
    methods: {
        ...mapActions("labels", ["getLabel"]),
        prettyFormatDate(date) {
          return moment(date).format('DD-MM-YYYY')
        },
        loadEvents() {
            this.showModalEvent = false;
            this.loadingEvents = true
            EventService.loadEvents(this.selectedLocationID, this.index, this.number).then(response => {
                this.events = this.filterEventsByClipodValidity(response.events)
                this.nbPages = response.nbPages
                this.loadingEvents = false
            })
        },
        // In case of Customer switch, we filter the view with validFrom/validTo
        filterEventsByClipodValidity(events) {
            if (!events) return events
            // Find clipod for selected location
            const clipod = this.clipods && this.clipods.find(
                c => c.pod && c.pod.location && c.pod.location.id == this.selectedLocationID
            )
            // No clipod found for this location
            if (!clipod) return events
            const validFrom = clipod.validFrom ? new Date(clipod.validFrom) : null
            const validTo = clipod.validTo ? new Date(clipod.validTo) : null
            return events.filter(event => {
                const eventDate = new Date(event.date)
                if (validFrom && eventDate < validFrom) return false
                if (validTo && eventDate > validTo) return false
                return true
            })
        },
        loadLocations(locationId = null) {
            AddressService.getAddresses(this.user._id).then(locations => {
                this.locations = locations;
                if (!locations || locations.length === 0) return;
                
                const location = locations.find(item => item.id == locationId)
                const locationPodId = sessionStorage.getItem("user-location")
                if (location) {
                  // Sélection d'une adresse
                  this.selectedLocationID = location.id
                  this.locationText = this.formatAddress(location)
                } else if (locationPodId) {
                  // Adresse par défaut en fonction du POD sélectionné
                  const defaultLocation = locations.find(item => item.id == locationPodId)
                  if (defaultLocation) {
                    this.selectedLocationID = locationPodId
                    this.locationText = this.formatAddress(defaultLocation)
                  }
                } else if (locations.length > 0) {
                  // Si pas d'adresse au POD, montrer la première
                  this.selectedLocationID = locations[0].id
                  this.locationText = this.formatAddress(locations[0])
                }
                this.loadEvents()
            })
              .finally(() => { this.loadedLocations = true })
        },
        deleteEvent(eventID) {
            if(!confirm(this.label.texts.deleteConfirm))
                return
            EventService.deleteEvent(eventID).then(() => {
                this.loadEvents()
            })
        },
        formatAddress(address) {
            return `${address.customName} : ${address.streetName} ${address.streetNumber} ${address.boxNumber ? address.boxNumber: ''} ${address.postalCode} ${address.localityName}`
        },
        createEventModal() {
          this.showCreationForm = true
          this.showModalEvent = true
        },
        editEventModal(event) {
          this.selectedEvent = {...event} // DeepCopy -> sans référence
          this.showCreationForm = false
          this.showModalEvent = true
        },
        closeModal() {
          this.showModalEvent = false;
        },
    },
    watch: {
      index(newVal) {
        if (newVal < 0) {
          this.index = 0
          return
        }
        this.loadEvents()
      },
      selectedLocationID() {
        this.index = 0
        this.loadEvents()
      }
    },
    created() {
        this.getLabel({ view: 'myEvents' })
        // Load clipods first to filter events by validFrom/validTo
        PODService.getActivePods(this.user._id, true)
            .then(clipods => { this.clipods = clipods || [] })
            .catch(() => { this.clipods = [] })
            .finally(() => this.loadLocations())
    },
}
</script>

<style lang="scss" scoped>
.table-container {
  margin-top: 30px;
}

.add-event {
  cursor: pointer;

  &:hover {
    background-color: #8796c9;
    color: white;
  }
}
</style>